import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  LinearProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Person,
  TrendingUp,
  CalendarToday,
  AttachMoney,
  ArrowUpward,
  Search,
  FilterList,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import typography from '../../cssUtils/typography';
import dayjs from "dayjs";
import AppDatePicker from "../../common/app-date-picker";
// FIX: no LocalizationProvider exists anywhere in the app's provider tree
// (verified: not in src/index.js, not in src/App.js). That absence - not a
// misplaced/too-narrow provider - is the direct cause of the MUI X
// "Can not find the date and time pickers localization context" runtime
// error. Rather than edit the shared app root (App.js), the provider is
// scoped locally to this component: one LocalizationProvider wraps this
// component's own render output below, not one per AppDatePicker instance.
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const URL = process.env.NODE_ENV === 'production' ? 'api' : `http://localhost:8000/api`;

const RMDashboardTab = () => {
  const [expandedCards, setExpandedCards] = useState({
    clientEngagement: true,
    interactionSummary: true,
    aumTrend: true,
    clientList: true
  });

  // Refs to the collapsible cards so we can scroll them into view on expand.
  const cardRefs = {
    aumTrend: useRef(null),
    clientEngagement: useRef(null),
    interactionSummary: useRef(null),
    clientList: useRef(null)
  };

  const [dashboardData, setDashboardData] = useState({
    rm_info: {},
    total_aum: 0,
    last_client_contact: null,
    engagement_metrics: {
      last_3_months: { engaged_clients: 0, total_clients: 0, percentage: 0, meeting_count: 0 },
      last_6_months: { engaged_clients: 0, total_clients: 0, percentage: 0, meeting_count: 0 },
      last_12_months: { engaged_clients: 0, total_clients: 0, percentage: 0, meeting_count: 0 }
    },
    clients: []
  });

  // Date filter state
  const [dateFilter, setDateFilter] = useState('latest');
  // FIX: customFromDate/customToDate now hold dayjs objects (or null), driven
  // by AppDatePicker, instead of raw native <input type="date"> string values.
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);

  // Mock data (same as provided)
  const profileData = {
    rmName: "Sarah Johnson",
    totalAUM: "₹2.45B",
    aumTrend: "+8.2%",
    lastFundAddition: {
      amount: "₹125M",
      date: "2025-01-15",
      trend: "+12.5%"
    },
    lastConversation: "2025-01-28",
    aumHistory: [
      { month: 'Aug', aum: 2.1 },
      { month: 'Sep', aum: 2.2 },
      { month: 'Oct', aum: 2.25 },
      { month: 'Nov', aum: 2.3 },
      { month: 'Dec', aum: 2.4 },
      { month: 'Jan', aum: 2.45 }
    ],
    clients: [
      {
        id: '1',
        name: 'John Smith',
        aum: '₹450M',
        lastContact: '2025-01-28',
        investmentType: 'Equity',
        status: 'active'
      },
      // ... other clients
    ]
  };

  // FIX: toggleCard now takes the card name, flips its expanded state, and -
  // only when the card is being EXPANDED (not collapsed) - schedules a smooth
  // scrollIntoView once the Collapse has had a chance to mount/measure its
  // content. A short timeout (matching MUI's default Collapse duration lead-in)
  // lets the DOM update before we measure position; block: "start" brings the
  // top of the card into view rather than centering it.
  const toggleCard = (cardName) => {
    setExpandedCards(prev => {
      const isExpanding = !prev[cardName];

      if (isExpanding) {
        // Defer until after this render/commit so the Collapse has started
        // rendering its content and the ref's bounding box is meaningful.
        requestAnimationFrame(() => {
          setTimeout(() => {
            cardRefs[cardName]?.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 50);
        });
      }

      return {
        ...prev,
        [cardName]: isExpanding
      };
    });
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to compute from_date/to_date for the non-custom filter options
  const getDateRange = (filterType) => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    if (filterType === 'today') {
      return { from_date: formatDate(today), to_date: formatDate(today) };
    }
    if (filterType === 'month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from_date: formatDate(firstDay), to_date: formatDate(today) };
    }
    if (filterType === 'quarter') {
      const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
      const firstDay = new Date(today.getFullYear(), quarterStartMonth, 1);
      return { from_date: formatDate(firstDay), to_date: formatDate(today) };
    }
    // 'latest' -> no range
    return null;
  };

  const fetchDashboardData = async (from_date, to_date) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const tenant = localStorage.getItem('tenant');
      const headers = {
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant
      };

      let url = `${URL}/rm-dashboard/rm/list/?rm_id=${user.id}`;
      if (from_date && to_date) {
        url += `&from_date=${from_date}&to_date=${to_date}`;
      }

      const response = await fetch(url, {headers});
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Refresh dashboard whenever the date filter (or custom dates) change
  useEffect(() => {
    if (dateFilter === 'custom') {
      if (customFromDate && customToDate) {
        // FIX: convert the AppDatePicker (dayjs) values to the YYYY-MM-DD
        // format the API expects, instead of sending the raw picker value.
        const from = dayjs(customFromDate).format('YYYY-MM-DD');
        const to = dayjs(customToDate).format('YYYY-MM-DD');
        fetchDashboardData(from, to);
      }
      return;
    }

    const range = getDateRange(dateFilter);
    if (range) {
      fetchDashboardData(range.from_date, range.to_date);
    } else {
      // 'latest' - preserve existing default behaviour
      fetchDashboardData();
    }
  }, [dateFilter, customFromDate, customToDate]);

  const handleFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  return (
    // FIX: LocalizationProvider wraps this component's whole render tree
    // (not each individual AppDatePicker) so both From/To pickers below
    // share the same single provider instance.
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box sx={{ p: 3, pt: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          // FIX: pulled further up per request - pt: 0 on the outer Box
          // (above) plus mt: -1 here shifts the whole filter row (Date
          // Range select + Reset button) higher, closer to the top edge.
          mb: 1.5,
          mt: -1,
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="rm-dashboard-date-filter-label">Date Range</InputLabel>
          <Select
            labelId="rm-dashboard-date-filter-label"
            label="Date Range"
            value={dateFilter}
            onChange={handleFilterChange}
          >
            <MenuItem value="latest">Latest</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="month">Month</MenuItem>
            <MenuItem value="quarter">Quarter</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        {dateFilter === "custom" && (
          <>
            {/* FIX: replaced native <TextField type="date"> with the project's
                existing AppDatePicker (DD/MM/YYYY display, dayjs values). */}
            <AppDatePicker
              label="From"
              value={customFromDate}
              onChange={(newValue) => {
                setCustomFromDate(newValue);
                // FIX: if the newly picked From date is after the currently
                // selected To date, clear To instead of allowing an invalid
                // range to be sent to the API.
                if (newValue && customToDate && dayjs(newValue).isAfter(customToDate)) {
                  setCustomToDate(null);
                }
              }}
              disableFuture
              // FIX: AppDatePicker defaults to fullWidth (100%), which was
              // never overridden here, so the picker stretched to fill the
              // whole row instead of sitting inline next to the Date Range
              // select and Reset button. fullWidth={false} uses the
              // component's own "fit-content" sizing (see app-date-picker.jsx),
              // no change to that shared component needed.
              fullWidth={false}
            />

            <AppDatePicker
              label="To"
              value={customToDate}
              onChange={(newValue) => setCustomToDate(newValue)}
              minDate={customFromDate || undefined}
              disableFuture
              // FIX: same reason as the From picker above.
              fullWidth={false}
            />
          </>
        )}

        <Button
          variant="outlined"
          size="small"
          sx={{ height: 40 }}
          onClick={() => {
            setDateFilter("latest");
            // FIX: reset to null (not "") to match the dayjs/AppDatePicker
            // empty-value contract used by customFromDate/customToDate.
            setCustomFromDate(null);
            setCustomToDate(null);
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Key Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* AUM Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Total AUM"
              action={<CurrencyRupeeIcon />}
              sx={{ ...typography.h1}}
            />
            <CardContent>
              <Typography variant="h5" sx={{ ...typography.h1}}>
                {formatCurrency(dashboardData.total_aum)}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ ...typography.bodyLarge}}>
                As of latest holding date
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Engagement */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Total Clients"
              action={<Person />}
              sx={{ ...typography.h1}}
            />
            <CardContent>
              <Typography variant="h5" sx={{ ...typography.h1}}>
                {dashboardData.clients?.length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ ...typography.bodyLarge}}>
                Active Clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Last Contact */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Last Client Contact"
              action={<CalendarToday />}
              sx={{ ...typography.h1}}
            />
            <CardContent>
              <Typography variant="h5" sx={{ ...typography.h1}}>
                {formatDate(dashboardData.last_client_contact)}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ ...typography.bodyLarge}}>
                {dashboardData.last_client_contact ? 'Last client interaction' : 'No recent activity'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* AUM Trend */}
      {/* FIX: attached cardRefs.aumTrend to the Card so toggleCard can scroll to it on expand. */}
      <Card ref={cardRefs.aumTrend} sx={{ mb: 3 }}>
        <CardHeader
          title="AUM Trend"
          action={
            <IconButton onClick={() => toggleCard('aumTrend')}>
              {expandedCards.aumTrend ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          }
          sx={{ ...typography.h1}}
        />
        <Collapse in={expandedCards.aumTrend}>
          <CardContent>
            <Box sx={{ width: '100%', height: 300, mx: 'auto' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={dashboardData.aumHistory}
                  margin={{ top: 10, right: 30, left: 20, bottom: 10 }} // Increased left margin for Y-axis
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => {
                      // Format large numbers with appropriate suffixes
                      if (value >= 10000000) return `${(value / 10000000).toFixed(2)}Cr`;
                      if (value >= 100000) return `${(value / 100000).toFixed(2)}L`;
                      if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
                      return value.toFixed(2);
                    }}
                    width={55} // Fixed width for Y-axis to ensure enough space
                  />
                    <Tooltip 
                      formatter={(value) => {
                        // Format full currency value
                        const formattedValue = new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 2
                        }).format(value);
                        
                        // Add appropriate suffix in brackets
                        let suffix = "";
                        if (value >= 10000000) { // 1 Crore = 10,000,000
                          suffix = ` (${(value / 10000000).toFixed(2)}Cr)`;
                        } else if (value >= 100000) { // 1 Lakh = 100,000
                          suffix = ` (${(value / 100000).toFixed(2)}L)`;
                        } else if (value >= 1000) {
                          suffix = ` (${(value / 1000).toFixed(2)}K)`;
                        }
                        
                        return [formattedValue + suffix, "AUM"];
                      }}
                    />
                  <Line type="monotone" dataKey="aum" stroke="#1976d2" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Collapse>
      </Card>


      {/* Client Engagement Overview and Interaction Summary */}
      {/* FIX: alignItems="flex-start" added so this Grid row no longer stretches both
          Card siblings to match the taller one's height. Previously, MUI's default
          flexbox "align-items: stretch" forced both cards to always share the same
          height, so collapsing one card left behind empty space (its Card container
          stayed stretched to match the still-expanded sibling) and produced
          inconsistent, jumpy collapse behavior between the two cards. */}
      <Grid container spacing={3} sx={{ mb: 3 }} alignItems="flex-start">
        {/* Client Engagement Overview */}
        {/* FIX: removed sx={{ display: 'flex' }} from Grid item - no longer needed
            once the container isn't stretching row height. */}
        <Grid item xs={12} md={6}>
          {/* FIX: removed display: 'flex', flexDirection: 'column' from Card sx -
              this was the flex-stretch styling that made the card force-fill the
              row height instead of sizing to its own (collapsible) content.
              Also attached cardRefs.clientEngagement for scroll-into-view on expand. */}
          <Card ref={cardRefs.clientEngagement} sx={{ width: '100%' }}>
            <CardHeader
              title="Client Engagement Overview"
              action={
                <IconButton onClick={() => toggleCard('clientEngagement')}>
                  {expandedCards.clientEngagement ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
              sx={{ ...typography.h1 }}
            />
            {/* FIX: unmountOnExit removes the collapsed content from the DOM entirely
                (instead of just hiding it at height 0), so no residual box/space is
                left behind. timeout="auto" lets MUI measure the actual content height
                for a smooth, correctly-timed collapse/expand animation instead of a
                fixed guessed duration - matching the Interaction Summary card exactly. */}
            <Collapse
              in={expandedCards.clientEngagement}
              timeout="auto"
              unmountOnExit
            >
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Engagement Metrics */}
                  {['last_3_months', 'last_6_months', 'last_12_months'].map((period) => (
                    <Box key={period} sx={{ ...typography.bodyLarge}}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium" >
                          {`Last ${period.split('_')[1]} Months:`} {dashboardData.engagement_metrics?.[period]?.engaged_clients || 0} / {dashboardData.engagement_metrics?.[period]?.total_clients || 0} clients
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dashboardData.clients?.length || 0} clients
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={dashboardData.engagement_metrics?.[period]?.percentage || 0}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        </Grid>

        {/* Interaction Summary */}
        {/* FIX: removed sx={{ display: 'flex' }} from Grid item - same reason as above. */}
        <Grid item xs={12} md={6}>
          {/* FIX: removed display: 'flex', flexDirection: 'column' from Card sx - same reason as above.
              Also attached cardRefs.interactionSummary for scroll-into-view on expand. */}
          <Card ref={cardRefs.interactionSummary} sx={{ width: '100%' }}>
            <CardHeader
              title="Interaction Summary"
              action={
                <IconButton onClick={() => toggleCard('interactionSummary')}>
                  {expandedCards.interactionSummary ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
              sx={{ ...typography.h1 }}
            />
            {/* FIX: same unmountOnExit + timeout="auto" as Client Engagement Overview,
                so both cards use the identical Collapse implementation. */}
            <Collapse
              in={expandedCards.interactionSummary}
              timeout="auto"
              unmountOnExit
            >
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Interaction Summary Metrics */}
                  {[
                    { label: 'Active Clients', key: 'active_clients', bgColor: '#dbeafe', iconColor: '#2563eb', Icon: Person },
                    { label: 'Meetings Conducted', key: 'meetings_conducted', bgColor: '#dcfce7', iconColor: '#16a34a', Icon: CalendarToday },
                    { label: 'Portfolio Reviews', key: 'portfolio_reviews', bgColor: '#f3e8ff', iconColor: '#9333ea', Icon: TrendingUp },
                  ].map(({ label, key, bgColor, iconColor, Icon }) => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        bgcolor: '#f8fafc',
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1, bgcolor: bgColor, borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                          <Icon sx={{ color: iconColor }} />
                        </Box>
                        <Box sx={{ ...typography.bodyLarge}}>
                          <Typography variant="body2" fontWeight="medium" >{label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {key === 'active_clients' ? 'Till date' : 'This month'}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6">
                        {dashboardData?.interaction_summary?.[key] === null ? 'N/A' : dashboardData?.interaction_summary?.[key]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        </Grid>
      </Grid>
    </Box>
    </LocalizationProvider>
  );
};

export default RMDashboardTab;